import React, { useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import EngineeringIcon from '@mui/icons-material/Engineering';
import MedicalServicesIcon from '@mui/icons-material/MedicalServices';
import NetworkWifiIcon from '@mui/icons-material/NetworkWifi';
import ShieldIcon from '@mui/icons-material/Shield';
import BedIcon from '@mui/icons-material/Bed';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import AccessibilityNewIcon from '@mui/icons-material/AccessibilityNew';
import { useTranslation } from 'react-i18next';
import HeroNavbarWithDrawer from './HeroNavbarWithDrawer';

const icons = [
  <BedIcon fontSize="inherit" />,
  <MedicalServicesIcon fontSize="inherit" />,
  <NetworkWifiIcon fontSize="inherit" />,
  <ShieldIcon fontSize="inherit" />,
  <EngineeringIcon fontSize="inherit" />,
  <LocalHospitalIcon fontSize="inherit" />,
  <AccessibilityNewIcon fontSize="inherit" />
];

const images = [
  "/image/S5.png",
  "/image/S1.png",
  "/image/S2.png",
  "/image/S3.png",
  "/image/S4.png",
  "/image/S6.png",
  "/image/S7.png"
];

const FullscreenServiceDetail = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { t, i18n } = useTranslation();
  const services = t('s', { returnObjects: true });

  // refs to each full-screen section
  const sectionRefs = useRef([]);

  // refs to store scroll position and ticking flag for rAF
  const latestScrollY = useRef(0);
  const ticking = useRef(false);

  useEffect(() => {
    const onScroll = () => {
      latestScrollY.current = window.pageYOffset;
      requestTick();
    };

    const requestTick = () => {
      if (!ticking.current) {
        requestAnimationFrame(updateParallax);
        ticking.current = true;
      }
    };

    const updateParallax = () => {
      const windowHeight = window.innerHeight;

      sectionRefs.current.forEach((section) => {
        if (!section) return;
        const offsetTop = section.offsetTop;
        const relativeY = latestScrollY.current - offsetTop;

        // Only update if section is near viewport for optimization
        if (relativeY >= -windowHeight && relativeY <= windowHeight) {
          const speed = 0.4; // parallax speed, adjust as you like
          const bgPosY = -relativeY * speed;

          section.style.backgroundPosition = `center ${bgPosY}px`;
        }
      });

      ticking.current = false;
    };

    window.addEventListener('scroll', onScroll, { passive: true });

    // Initial background position set
    updateParallax();

    return () => {
      window.removeEventListener('scroll', onScroll);
    };
  }, []);

  return (
    <div>
      <HeroNavbarWithDrawer />
      <Box
        sx={{
          height: '100vh',
          width: '100%',
          overflowY: 'scroll',
          overflowX: 'hidden',
          scrollSnapType: 'y mandatory',
          scrollBehavior: 'smooth',
          position: 'relative',
          backgroundColor: '#000',
          direction: i18n.language === 'ar' ? 'rtl' : 'ltr',
          scrollbarWidth: 'thin',
        }}
      >
        {services.map((service, index) => (
          <Box
            key={index}
            ref={el => (sectionRefs.current[index] = el)}
            sx={{
              height: '100vh',
              width: '100%',
              scrollSnapAlign: 'start',
              backgroundImage: `url(${images[index]})`,
              backgroundSize: 'cover',
              backgroundRepeat: 'no-repeat',
              backgroundPosition: 'center 0px', // this will be updated on scroll
              position: 'relative',
              display: 'flex',
              alignItems: 'center',
              justifyContent: isMobile ? 'center' : 'flex-start',
              px: isMobile ? 2 : 10,
              py: isMobile ? 4 : 8,
              '&::before': {
                content: '""',
                position: 'absolute',
                inset: 0,
                background: `linear-gradient(to bottom, rgba(0,0,0,0.5), rgba(0,0,0,0.6))`,
                pointerEvents: 'none',
                zIndex: 1,
              },
            }}
          >
            <Box
              sx={{
                position: 'relative',
                zIndex: 2,
                backdropFilter: 'blur(12px)',
                backgroundColor: 'rgba(255, 255, 255, 0.07)',
                borderRadius: '20px',
                padding: isMobile ? 2 : 5,
                maxWidth: isMobile ? '90%' : '700px',
                color: '#fff',
                border: '1px solid rgba(255, 255, 255, 0.25)',
                boxShadow: '0 20px 60px rgba(0,0,0,0.4)',
                animation: `fadeZoomIn 0.8s ease-out forwards`,
                animationDelay: `${index * 0.3}s`,
                transform: 'translateY(30px)',
                opacity: 0,
                borderTop: `8px solid #CE8238`,
                borderLeft: `8px solid #0057A4`,
                textAlign: i18n.language === 'ar' ? 'right' : 'left',
              }}
            >
              <Box fontSize={isMobile ? 40 : 70} mb={2}>
                {icons[index]}
              </Box>
              <Typography variant={isMobile ? 'h6' : 'h3'} fontWeight="bold" mb={2}>
                {service.title}
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  fontSize: isMobile ? '0.95rem' : '1.15rem',
                  lineHeight: 1.7,
                  whiteSpace: 'pre-line',
                }}
              >
                {service.description}
              </Typography>
            </Box>
          </Box>
        ))}

        <style>{`
          @keyframes fadeZoomIn {
            0% {
              opacity: 0;
              transform: translateY(30px) scale(0.95);
            }
            100% {
              opacity: 1;
              transform: translateY(0px) scale(1);
            }
          }
          ::-webkit-scrollbar {
            width: 8px;
          }
          ::-webkit-scrollbar-thumb {
            background: rgba(255, 255, 255, 0.2);
            border-radius: 4px;
          }
        `}</style>
      </Box>
    </div>
  );
};

export default FullscreenServiceDetail;
